import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Briefcase, Heart, TrendingUp, Users, Coffee, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const benefits = [
  {
    icon: Heart,
    title: 'Health & Wellness',
    description: 'Comprehensive medical, dental, and vision coverage for you and your family.',
  },
  {
    icon: TrendingUp,
    title: 'Growth & Development',
    description: 'Professional development programs, tuition reimbursement, and career advancement.',
  },
  {
    icon: Users,
    title: 'Work-Life Balance',
    description: 'Flexible work arrangements, generous PTO, and parental leave.',
  },
  {
    icon: Coffee,
    title: 'Perks & Culture',
    description: '401(k) matching, wellness programs, team events, and modern offices.',
  },
];

const jobs = [
  {
    id: 1,
    title: 'Senior Revenue Cycle Analyst',
    department: 'Revenue Cycle',
    location: 'Brentwood, TN',
    type: 'Full-time',
    experience: '5+ years',
    posted: '2 days ago',
  },
  {
    id: 2,
    title: 'Healthcare Credentialing Specialist',
    department: 'Credentialing',
    location: 'Remote',
    type: 'Full-time',
    experience: '3+ years',
    posted: '1 week ago',
  },
  {
    id: 3,
    title: 'Practice Management Consultant',
    department: 'Consulting',
    location: 'Wilmington, DE',
    type: 'Full-time',
    experience: '7+ years',
    posted: '3 days ago',
  },
  {
    id: 4,
    title: 'Medical Billing Team Lead',
    department: 'Revenue Cycle',
    location: 'Brentwood, TN',
    type: 'Full-time',
    experience: '4+ years',
    posted: '5 days ago',
  },
  {
    id: 5,
    title: 'Healthcare IT Project Manager',
    department: 'Technology',
    location: 'Remote',
    type: 'Full-time',
    experience: '5+ years',
    posted: '1 week ago',
  },
  {
    id: 6,
    title: 'Compliance Analyst',
    department: 'Compliance',
    location: 'Wilmington, DE',
    type: 'Full-time',
    experience: '2+ years',
    posted: '4 days ago',
  },
];

const departments = ['All Departments', 'Revenue Cycle', 'Credentialing', 'Consulting', 'Technology', 'Compliance'];
const locations = ['All Locations', 'Brentwood, TN', 'Wilmington, DE', 'Remote'];

export const Careers = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All Departments' || job.department === selectedDepartment;
    const matchesLocation = selectedLocation === 'All Locations' || job.location === selectedLocation;
    return matchesSearch && matchesDepartment && matchesLocation;
  });

  return (
    <div data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-navy overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1590650213165-c1fef80648c4?w=1920&q=80"
            alt="Professional in modern workplace"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Careers
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Build Your Career in Healthcare Operations
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed mb-8">
              Join a team of passionate professionals dedicated to transforming healthcare management. We're looking for talented individuals who share our vision.
            </p>
            <Button 
              className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="careers-view-jobs-btn"
              onClick={() => document.getElementById('job-listings').scrollIntoView({ behavior: 'smooth' })}
            >
              View Open Positions
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white" data-testid="careers-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '250+', label: 'Team Members' },
              { value: '4.8', label: 'Glassdoor Rating' },
              { value: '15+', label: 'Years in Business' },
              { value: '95%', label: 'Employee Satisfaction' },
            ].map((stat) => (
              <div key={stat.label} data-testid={`careers-stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="stat-value text-3xl md:text-4xl font-bold text-navy mb-2">{stat.value}</div>
                <div className="text-sm text-navy-600 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-navy-50" data-testid="careers-benefits">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Why Join Us
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-4">
              Benefits & Perks
            </h2>
            <p className="text-navy-600">
              We invest in our team because we know great people drive great results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => (
              <Card 
                key={benefit.title}
                className="bg-white border-silver-light shadow-card card-hover"
                data-testid={`benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-sm bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-emerald" />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{benefit.title}</h3>
                  <p className="text-sm text-navy-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="section-padding bg-white" data-testid="careers-culture">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
                Our Culture
              </span>
              <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-6">
                Where Passion Meets Purpose
              </h2>
              <p className="text-navy-600 leading-relaxed mb-6">
                At HealthCheck Medical, we believe in fostering an environment where innovation thrives, collaboration is encouraged, and every team member has the opportunity to make a meaningful impact.
              </p>
              <p className="text-navy-600 leading-relaxed mb-8">
                Our diverse team brings together healthcare experts, technologists, and business strategists who share a common goal: helping healthcare organizations succeed.
              </p>
              <ul className="space-y-4">
                {[
                  'Collaborative, supportive team environment',
                  'Opportunities for professional growth',
                  'Meaningful work with real impact',
                  'Inclusive and diverse workplace',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-navy-700">
                    <div className="w-2 h-2 rounded-full bg-emerald"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1590649681928-4b179f773bd5?w=400&q=80"
                alt="Team meeting"
                className="w-full h-48 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1749310726959-d8fccfef7ee4?w=400&q=80"
                alt="Modern corporate lobby"
                className="w-full h-48 object-cover mt-8"
              />
              <img 
                src="https://images.unsplash.com/photo-1637665662134-db459c1bbb46?w=400&q=80"
                alt="Executive boardroom"
                className="w-full h-48 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1624213012413-fda54df1810f?w=400&q=80"
                alt="Modern office building"
                className="w-full h-48 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section id="job-listings" className="section-padding bg-navy-50" data-testid="job-listings">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Open Positions
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              Current Opportunities
            </h2>
          </div>

          {/* Filters */}
          <div className="bg-white p-6 mb-8 flex flex-col md:flex-row gap-4" data-testid="job-filters">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type="text"
                placeholder="Search positions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-none border-silver h-11"
                data-testid="job-search-input"
              />
            </div>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="h-11 px-4 border border-silver bg-white text-navy-700 text-sm"
              data-testid="job-department-filter"
            >
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="h-11 px-4 border border-silver bg-white text-navy-700 text-sm"
              data-testid="job-location-filter"
            >
              {locations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* Job Cards */}
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card 
                key={job.id}
                className="bg-white border-silver-light shadow-card card-hover"
                data-testid={`job-${job.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-navy-600">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-navy-500">
                        {job.experience} experience • Posted {job.posted}
                      </div>
                    </div>
                    <Button 
                      className="bg-navy hover:bg-emerald text-white rounded-none font-semibold tracking-wide uppercase text-xs h-10 px-6"
                      data-testid={`apply-job-${job.id}`}
                    >
                      Apply Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12 bg-white">
              <p className="text-navy-600">No positions found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald" data-testid="careers-cta">
        <div className="container-custom text-center">
          <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Don't See the Right Fit?
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <Button 
            asChild
            className="bg-white text-emerald hover:bg-navy hover:text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
            data-testid="careers-contact-btn"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
